import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "./baseQueryWithReauth";
import { TimeRangeValue } from "@/types/timeRanges";

export interface FocusListItem {
  uid: string;
  symbol: string;
  name: string;
  type: "STOCK" | "ETF" | "INDEX";
  currentPrice: number;
  previousPrice: number;
  totalLikes: number;
  totalPosts: number;
  sentiment: number;
  isInFocusList: boolean;
  sector?: string;
  industry?: string;
  marketCap?: number;
  lastFundamentalsUpdate?: string;
  description?: string;
  category?: string;
  exchange?: string;
  etfCompanyName?: string;
  notes?: string;
  isVisible?: boolean;
  detailedAnalysis?: string;
}

export interface FocusListResponse {
  items: FocusListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface FocusListParams {
  type?: string;
  page?: number;
  pageSize?: number;
  date?: string;
  sectors?: string[];
  industries?: string[];
  categories?: string[];
  sortBy?: "post" | "likes" | "sentiment";
  order?: "asc" | "desc";
  time_range?: TimeRangeValue;
  _refresh?: number;
}

export const focusListApi = createApi({
  reducerPath: "focusListApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["FocusList"],
  endpoints: (build) => ({
    getFocusList: build.query<FocusListResponse, FocusListParams>({
      query: (params: FocusListParams) => {
        const queryString = new URLSearchParams();
        if (params.page) queryString.append("page", params.page.toString());
        if (params.pageSize) queryString.append("pageSize", params.pageSize.toString());
        if (params.type) queryString.append("type", params.type);
        if (params.date) queryString.append("date", params.date);
        if (params.sortBy) queryString.append("sortBy", params.sortBy);
        if (params.time_range) queryString.append("time_range", params.time_range);

        if (params.sectors?.length) {
          params.sectors.forEach((sector) => queryString.append("sectors", sector));
        }
        if (params.industries?.length) {
          params.industries.forEach((industry) => queryString.append("industries", industry));
        }
        if (params.categories?.length) {
          params.categories.forEach((category) => queryString.append("categories", category));
        }
        if (params.order) queryString.append("order", params.order);

        return {
          url: `/watchlist/focus${queryString.toString() ? `?${queryString.toString()}` : ""}`,
          method: "GET",
        };
      },

      providesTags: ["FocusList"],
    }),

    toggleFocus: build.mutation<boolean, string>({
      query: (symbol) => ({
        url: `/watchlist/focus/${symbol}`,
        method: "POST",
      }),
      invalidatesTags: ["FocusList"],
    }),

    removeFromFocus: build.mutation<boolean, string>({
      query: (uid) => ({
        url: `/watchlist/focus/${uid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["FocusList"],
    }),
  }),
});

export const { useGetFocusListQuery, useToggleFocusMutation, useRemoveFromFocusMutation } =
  focusListApi;
